import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  downloadSuperAdminCouponApplicationsCsv,
  getSuperAdminCouponApplications,
  SaCoupon,
  SaCouponApplication,
} from "../../../helpers/super_admin_coupons_helper";
import { formatCents, intlLocaleFromI18n } from "../../../helpers/money";

type Props = {
  isOpen: boolean;
  coupon: SaCoupon | null;
  onClose: () => void;
};

const PER_PAGE = 25;

type StatusFilter = "" | "active" | "completed" | "canceled";

/** Badge ops: active = desconto em curso; completed = ciclos esgotados; canceled = interrompido. */
const applicationStatusColor = (status: string): string => {
  switch (status) {
    case "active":
      return "success";
    case "completed":
      return "secondary";
    case "canceled":
      return "danger";
    default:
      return "light";
  }
};

/**
 * Modal de resgates — lista + link Cliente + export CSV (DOC 02 § 4.4 · LAC-082).
 * LAC-091: filtros search (nome tenant), status (active|completed|canceled).
 */
const CouponApplicationsModal = ({ isOpen, coupon, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const locale = intlLocaleFromI18n(i18n.language);
  const [applications, setApplications] = useState<SaCouponApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  // LAC-091: filtros de busca e status.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");

  const load = useCallback(async () => {
    if (!coupon?.id) return;

    setLoading(true);
    setError(null);

    try {
      const res = await getSuperAdminCouponApplications(coupon.id, {
        page,
        per_page: PER_PAGE,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
      });

      if (!res?.success) {
        setError(t("sa.coupons.applications.error.load"));
        setApplications([]);
        setTotal(0);
        setLastPage(1);
        return;
      }

      setApplications(res.data || []);
      setTotal(res.meta?.total ?? (res.data || []).length);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (err: any) {
      setError(err?.message || t("sa.coupons.applications.error.load"));
      setApplications([]);
      setTotal(0);
      setLastPage(1);
    } finally {
      setLoading(false);
    }
  }, [coupon?.id, page, searchTerm, statusFilter, t]);

  useEffect(() => {
    if (!isOpen || !coupon) {
      setApplications([]);
      setError(null);
      setPage(1);
      setLastPage(1);
      setTotal(0);
      setExporting(false);
      // LAC-091: reset filtros ao fechar.
      setSearchInput("");
      setSearchTerm("");
      setStatusFilter("");
      return;
    }
    void load();
  }, [isOpen, coupon, load]);

  // LAC-091: reset page ao mudar filtros (debounce no search).
  const handleSearchSubmit = () => {
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleStatusChange = (next: StatusFilter) => {
    setPage(1);
    setStatusFilter(next);
  };

  // Indicador de filtros ativos para empty state diferenciado.
  const hasFilters = searchTerm !== "" || statusFilter !== "";

  const formatAppliedAt = (iso: string | null) => {
    if (!iso) return "—";
    try {
      return new Intl.DateTimeFormat(locale, {
        dateStyle: "short",
        timeStyle: "short",
        timeZone: "UTC",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const handleExport = async () => {
    if (!coupon?.id || exporting) return;

    setExporting(true);
    try {
      // LAC-091: exporta CSV com os mesmos filtros aplicados na visualização.
      await downloadSuperAdminCouponApplicationsCsv(
        coupon.id,
        `coupon-${coupon.code}-applications.csv`,
        {
          search: searchTerm || undefined,
          status: statusFilter || undefined,
        }
      );
      toast.success(t("sa.coupons.applications.export.success"), {
        autoClose: 2500,
      });
    } catch (err: any) {
      toast.error(err?.message || t("sa.coupons.applications.export.error"), {
        autoClose: 4000,
      });
    } finally {
      setExporting(false);
    }
  };

  const clientLabel = (row: SaCouponApplication) => {
    if (row.tenant?.name) {
      return row.tenant.name;
    }
    return `#${row.tenant_id}`;
  };

  const clientId = (row: SaCouponApplication) =>
    row.tenant?.id ?? row.tenant_id;

  const statusLabel = (status: string) =>
    t(`sa.coupons.applications.status.${status}`, { defaultValue: status });

  /** null = lifetime; número = faturas promocionais restantes. */
  const cyclesLabel = (row: SaCouponApplication) => {
    if (row.cycles_remaining === null || row.cycles_remaining === undefined) {
      return t("sa.coupons.applications.cycles.lifetime");
    }
    return t("sa.coupons.applications.cycles.remaining", {
      count: row.cycles_remaining,
    });
  };

  const title = t("sa.coupons.applications.title", {
    code: coupon?.code ?? "",
  });

  const canExport = total > 0 && !loading && !exporting;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="xl">
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {/* LAC-091: filtros de busca e status. */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <InputGroup style={{ maxWidth: 280 }}>
            <Input
              type="text"
              placeholder={t("sa.coupons.applications.filter.searchPlaceholder")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchSubmit();
                }
              }}
              disabled={loading}
            />
            <Button
              color="light"
              onClick={handleSearchSubmit}
              disabled={loading}
              title={t("sa.coupons.applications.filter.searchButton")}
            >
              <i className="ri-search-line" aria-hidden="true" />
            </Button>
          </InputGroup>
          <Input
            type="select"
            bsSize="sm"
            style={{ maxWidth: 200, height: "auto" }}
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value as StatusFilter)}
            disabled={loading}
            aria-label={t("sa.coupons.applications.filter.status")}
          >
            <option value="">{t("sa.coupons.applications.filter.statusAll")}</option>
            <option value="active">{t("sa.coupons.applications.status.active")}</option>
            <option value="completed">{t("sa.coupons.applications.status.completed")}</option>
            <option value="canceled">{t("sa.coupons.applications.status.canceled")}</option>
          </Input>
          {hasFilters ? (
            <Button
              color="light"
              size="sm"
              onClick={() => {
                setSearchInput("");
                setSearchTerm("");
                setStatusFilter("");
                setPage(1);
              }}
              disabled={loading}
            >
              {t("sa.coupons.applications.filter.clear")}
            </Button>
          ) : null}
        </div>

        {error ? (
          <Alert color="danger" className="py-2">
            {error}
          </Alert>
        ) : null}

        {loading && applications.length === 0 ? (
          <div className="text-center py-4" role="status">
            <Spinner color="primary" className="me-2" />
            <span className="text-muted">
              {t("sa.coupons.applications.loading")}
            </span>
          </div>
        ) : applications.length === 0 && !error ? (
          <div className="text-center py-4 px-3">
            <div className="avatar-md mx-auto mb-3">
              <div className="avatar-title bg-light text-muted rounded-circle fs-24">
                <i className={hasFilters ? "ri-filter-off-line" : "ri-coupon-line"} aria-hidden="true" />
              </div>
            </div>
            <h6 className="mb-1">
              {hasFilters
                ? t("sa.coupons.applications.empty.filteredTitle")
                : t("sa.coupons.applications.empty.title")}
            </h6>
            <p className="text-muted small mb-0">
              {hasFilters
                ? t("sa.coupons.applications.empty.filteredDescription")
                : t("sa.coupons.applications.empty.description")}
            </p>
            {hasFilters ? (
              <Button
                color="light"
                size="sm"
                className="mt-3"
                onClick={() => {
                  setSearchInput("");
                  setSearchTerm("");
                  setStatusFilter("");
                  setPage(1);
                }}
              >
                {t("sa.coupons.applications.filter.clear")}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="table-responsive position-relative">
            {loading ? (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                style={{ zIndex: 2 }}
              >
                <Spinner color="primary" size="sm" />
              </div>
            ) : null}
            <Table className="align-middle mb-0" size="sm">
              <thead className="table-light">
                <tr>
                  <th scope="col">
                    {t("sa.coupons.applications.table.client")}
                  </th>
                  <th scope="col">
                    {t("sa.coupons.applications.table.subscription")}
                  </th>
                  <th scope="col">
                    {t("sa.coupons.applications.table.amount")}
                  </th>
                  <th scope="col">
                    {t("sa.coupons.applications.table.status")}
                  </th>
                  <th scope="col">
                    {t("sa.coupons.applications.table.cycles")}
                  </th>
                  <th scope="col">
                    {t("sa.coupons.applications.table.appliedAt")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {applications.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {/* Nova aba: ops não perde o modal de resgates. */}
                      <Link
                        to={`/super-admin/tenants/${clientId(row)}`}
                        className="fw-medium"
                        title={t("sa.coupons.applications.openClient")}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {clientLabel(row)}
                        <i
                          className="ri-external-link-line ms-1 small text-muted"
                          aria-hidden="true"
                        />
                      </Link>
                      <div className="text-muted small">#{clientId(row)}</div>
                    </td>
                    <td>#{row.subscription_id}</td>
                    <td className="text-nowrap">
                      {formatCents(row.amount_cents, "BRL", locale)}
                    </td>
                    <td>
                      <Badge
                        color={applicationStatusColor(row.status)}
                        className="text-uppercase"
                      >
                        {statusLabel(row.status)}
                      </Badge>
                    </td>
                    <td className="text-nowrap">{cyclesLabel(row)}</td>
                    <td className="text-nowrap">
                      {formatAppliedAt(row.applied_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {lastPage > 1 ? (
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
            <span className="text-muted small">
              {t("sa.coupons.applications.pagination.label", {
                page,
                lastPage,
                total,
              })}
            </span>
            <div className="d-flex gap-2">
              <Button
                color="light"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t("sa.coupons.pagination.prev")}
              </Button>
              <Button
                color="light"
                size="sm"
                disabled={page >= lastPage || loading}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              >
                {t("sa.coupons.pagination.next")}
              </Button>
            </div>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter className="d-flex flex-wrap gap-2 justify-content-between">
        <Button
          color="soft-primary"
          disabled={!canExport}
          onClick={() => void handleExport()}
          aria-label={t("sa.coupons.applications.export.button")}
        >
          {exporting ? (
            <>
              <Spinner size="sm" className="me-1" />
              {t("sa.coupons.applications.export.working")}
            </>
          ) : (
            <>
              <i className="ri-download-2-line me-1" aria-hidden="true" />
              {t("sa.coupons.applications.export.button")}
            </>
          )}
        </Button>
        <Button color="light" onClick={onClose}>
          {t("sa.coupons.applications.close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CouponApplicationsModal;
